import type { MemberSignInMethod } from "./index";

export const authPresentationPaths = [
  "/sign-in",
  "/sign-up",
  "/forgot-password",
  "/reset-password",
];
export const isAuthPresentationPath = (path: string) => authPresentationPaths.includes(path);

export const signInErrors: Record<string, string> = {
  credentials: "That email or password didn’t match. Please try again.",
  account: "We couldn’t create that account. Try another email or sign in instead.",
  email: "We couldn’t send that sign-in email. Please check the address and try again.",
  link: "That sign-in link is invalid or expired. Request a fresh link and try again.",
  phone: "We couldn’t send a code to that number. Check the number and try again.",
  code: "That code is invalid or expired. Check it or request a new code.",
  google: "Google sign-in could not be completed. Please try again.",
  facebook: "Facebook sign-in could not be completed. Please try another sign-in method.",
  "facebook-email":
    "Facebook didn’t share an email address. Allow email access in Facebook or choose another sign-in method.",
  discord: "Discord sign-in could not be completed. Please try again.",
  "discord-email": "Add and verify an email in Discord, then try again.",
  patreon: "Patreon sign-in could not be completed. Please try again.",
  rate: "Too many attempts. Wait a few minutes, then try again.",
};

/** Return targets are navigation hints, never cross-origin destinations or authorization. */
export function authReturnPath(value: string | null) {
  if (
    !value ||
    value.length > 2000 ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.includes("\\") ||
    [...value].some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)
  )
    return "/account";
  const url = new URL(value, "https://site.invalid");
  return url.origin === "https://site.invalid" ? url.pathname + url.search : "/account";
}

export function signInPresentationInput(signUp: boolean) {
  return { signUp, returnTo: null, method: null, sent: null, error: null, reset: false };
}

/** The public form settings are prepared once. URL-specific UI is selected in the browser. */
export function bindSignInQuery<
  T extends {
    methods: MemberSignInMethod[];
    method: MemberSignInMethod | null;
    returnTo: string;
    sent: string | null;
    errorMessage: string;
    reset: boolean;
  },
>(view: T, query: URLSearchParams): T {
  const method = query.get("method") as MemberSignInMethod | null;
  const error = query.get("error");
  return {
    ...view,
    returnTo: authReturnPath(query.get("returnTo")),
    method: method && method !== "discord" && view.methods.includes(method) ? method : view.method,
    sent: ["email", "phone"].includes(query.get("sent") ?? "") ? query.get("sent") : null,
    errorMessage: error ? signInErrors[error] || "We couldn’t sign you in. Please try again." : "",
    reset: query.get("reset") === "1",
  };
}
